import { useMemo, useState } from 'react';
import {
  ComboBox,
  InlineLoading,
  InlineNotification,
  Link,
  Layer,
} from '@carbon/react';
import { Link as RouterLink } from 'components/link';
import {
  PackageResponse,
  PackageTitleResponse,
  VestingDocumentSummary,
} from 'types/packages-api/responses';
import { Date } from 'components/date';
import { SellersCell } from 'pages/packages/list/sellers-cell';
import style from './packages-summary.module.scss';
import { createResourceAtom } from 'lib/atoms';
import { useAtomValue } from 'jotai';
import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';

const NO_DATA = '--';

// Three, then a count. The summary's other rows are single values, so an uncapped list would let
// one package stretch the block past everything around it — a deal whose tracts came from many
// instruments is ordinary.
const VESTING_DOCUMENTS_SHOWN = 3;

/**
 * How a deed reads on one line: the recording vocabulary when it has been extracted, a neutral
 * label while it has not. Matches the tract page's heading rule, so the same document does not
 * announce itself differently on two surfaces.
 * @param {VestingDocumentSummary} document - The deed to label.
 * @return {string} The label.
 */
const vestingDocumentLabel = (document: VestingDocumentSummary): string => {
  const citation =
    document.volume && document.page
      ? `Vol ${document.volume}, Pg ${document.page}`
      : document.instrumentNumber && `#${document.instrumentNumber}`;

  return [document.documentType || 'Recorded document', citation]
    .filter(Boolean)
    .join(' — ');
};

/**
 * The deeds that vest this package's tracts.
 *
 * Renders nothing at all when the field is absent, which is how a package with no deal arrives:
 * the payload omits it rather than sending an empty list, and a package that never had a deal
 * should not be told it is missing a deed. An empty list is different — the deal exists and
 * nothing has been linked to it yet, which is worth saying out loud.
 * @param {object} props - The component props.
 * @param {VestingDocumentSummary[] | undefined} props.documents - The deeds, if the payload has them.
 * @return {JSX.Element | null} The summary row.
 */
const VestingDocuments = ({
  documents,
}: {
  documents: VestingDocumentSummary[] | undefined;
}) => {
  if (!documents) return null;

  const shown = documents.slice(0, VESTING_DOCUMENTS_SHOWN);
  const remaining = documents.length - shown.length;

  return (
    <dl>
      <dt>Vesting Document</dt>
      <dd>
        {documents.length === 0
          ? 'None yet'
          : shown.map((document, index) => (
              <span key={document.id}>
                {index > 0 && ', '}
                <RouterLink
                  to={document.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vestingDocumentLabel(document)}
                </RouterLink>
              </span>
            ))}
        {remaining > 0 && ` +${remaining} more`}
      </dd>
    </dl>
  );
};

const TitleDetails = ({ href }: { href: string }) => {
  const titleDetailsAtom = useMemo(
    () => createResourceAtom<PackageTitleResponse[]>(href),
    [href]
  );
  const titleDetails = useAtomValue(titleDetailsAtom);

  if (titleDetails.data && 'error' in titleDetails.data)
    return (
      <InlineNotification
        kind="error"
        title={titleDetails.data.error}
        lowContrast
      />
    );

  if (titleDetails.loading) return <InlineLoading />;
  if (!titleDetails.data) return null;
  const data = titleDetails.data;

  return (
    <Layer>
      <ComboBox
        id="package-title-details"
        items={data || []}
        placeholder="Search"
        itemToString={(data) => {
          return data ? data.name : '';
        }}
        onChange={({ selectedItem }) => {
          if (selectedItem)
            window.open(selectedItem.href, '_blank', 'noopener,noreferrer');
        }}
      />
    </Layer>
  );
};

const PackagesSummaryTable = ({
  packageData,
}: {
  packageData: PackageResponse;
}) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const note = packageData.notes || NO_DATA;
  const noteText =
    note.length <= 170 ? note : showMore ? note : `${note.slice(0, 170)} ...`;

  return (
    <div className={style.packagesSummaryData}>
      {packageData.opportunityDeleted && (
        <InlineNotification
          kind="warning"
          title="This opportunity has been deleted from Salesforce."
          subtitle="Some data (seller, acquirer, acreage, maps) may be unavailable."
          lowContrast
          hideCloseButton
        />
      )}
      {packageData.resources.entity && (
        <dl>
          <dt>Account</dt>
          <dd>
            <RouterLink
              to={packageData.resources.entity.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {packageData.resources.entity.name}
            </RouterLink>
          </dd>
        </dl>
      )}
      {packageData.sellers.length > 0 && (
        <dl>
          <dt>Seller</dt>
          <dd>
            <SellersCell sellers={packageData.sellers} />
          </dd>
        </dl>
      )}
      {packageData.acquirerName && (
        <dl>
          <dt>Acquire Entity</dt>
          <dd>
            {packageData.acquirerEntities.length > 0
              ? packageData.acquirerEntities.map((entity, idx) => (
                  <span key={entity.id}>
                    {idx > 0 && ', '}
                    <RouterLink
                      to={entity.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entity.name}
                    </RouterLink>
                  </span>
                ))
              : packageData.acquirerName}
          </dd>
        </dl>
      )}
      {packageData.closeDate && (
        <dl>
          <dt>Close Date</dt>
          <dd>
            <Date date={packageData.closeDate} />
          </dd>
        </dl>
      )}
      {packageData.totalAcreage > 0 && (
        <dl>
          <dt>Total Acreage</dt>
          <dd>
            {DECIMAL_FORMATTER_TWO_FRACTIONS.format(packageData.totalAcreage)}
          </dd>
        </dl>
      )}
      {packageData.effectiveDate && (
        <dl>
          <dt>Effective Date</dt>
          <dd>
            <Date date={packageData.effectiveDate} />
          </dd>
        </dl>
      )}
      <VestingDocuments documents={packageData.vestingDocuments} />
      {packageData.includeChildren !== null && (
        <dl>
          <dt>Include Children</dt>
          <dd>{packageData.includeChildren ? 'Yes' : 'No'}</dd>
        </dl>
      )}
      <dl>
        <dt>Notes</dt>
        <dd className={style.packageNotes}>
          <p>{noteText}</p>
          {note.length > 170 && (
            <Link onClick={() => setShowMore(!showMore)}>
              {showMore ? 'Show less' : 'Show more'}
            </Link>
          )}
        </dd>
      </dl>
      <dl className={style.titleDetailsDropdown}>
        <dt>Title Details</dt>
        <dd>
          <TitleDetails href={packageData.resources.title.href} />
        </dd>
      </dl>
    </div>
  );
};

export { PackagesSummaryTable };
